// src/components/notifications/NotificationItem.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  Typography,
  IconButton,
  Chip,
  useTheme,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Info,
  Warning,
  Error as ErrorIcon,
  CheckCircle,
  Assignment,
  Schedule,
  Build,
  SystemUpdate,
  OpenInNew,
} from '@mui/icons-material';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';
import { tokens } from '../../../theme';

const getNotificationIcon = (type, colors) => {
  const iconProps = { sx: { fontSize: 18 } };
  switch (type) {
    case 'info':
      return <Info sx={{ color: colors.blueAccent[500], ...iconProps.sx }} />;
    case 'warning':
      return <Warning sx={{ color: colors.yellowAccent[500], ...iconProps.sx }} />;
    case 'error':
      return <ErrorIcon sx={{ color: colors.redAccent[500], ...iconProps.sx }} />;
    case 'success':
      return <CheckCircle sx={{ color: colors.greenAccent[500], ...iconProps.sx }} />;
    case 'task_assigned':
      return <Assignment sx={{ color: colors.blueAccent[500], ...iconProps.sx }} />;
    case 'task_completed':
      return <CheckCircle sx={{ color: colors.greenAccent[500], ...iconProps.sx }} />;
    case 'deadline_reminder':
      return <Schedule sx={{ color: colors.yellowAccent[500], ...iconProps.sx }} />;
    case 'system_update':
      return <SystemUpdate sx={{ color: colors.blueAccent[500], ...iconProps.sx }} />;
    case 'maintenance':
      return <Build sx={{ color: colors.yellowAccent[500], ...iconProps.sx }} />;
    default:
      return <Info sx={{ color: colors.grey[500], ...iconProps.sx }} />;
  }
};

const getPriorityColors = (priority, colors) => {
  switch (priority) {
    case 'urgent':
      return {
        color: colors.redAccent[500],
        backgroundColor: colors.redAccent[900],
        borderColor: colors.redAccent[700],
      };
    case 'high':
      return {
        color: colors.yellowAccent[400],
        backgroundColor: colors.yellowAccent[900],
        borderColor: colors.yellowAccent[700],
      };
    case 'normal':
      return {
        color: colors.blueAccent[500],
        backgroundColor: colors.blueAccent[900],
        borderColor: colors.blueAccent[700],
      };
    case 'low':
      return {
        color: colors.grey[500],
        backgroundColor: colors.grey[800],
        borderColor: colors.grey[600],
      };
    default:
      return {
        color: colors.grey[500],
        backgroundColor: colors.grey[800],
        borderColor: colors.grey[600],
      };
  }
};

// Helper hook to translate notification with fallback
const useNotificationTranslation = () => {
  const { t } = useTranslation(['notifications']);

  const translateNotification = (notification) => {
    // Prefer translation key, fallback to raw text from database
    const title = notification.title_key 
      ? t(notification.title_key, { 
          ...notification.translation_params,
          defaultValue: notification.title // Fallback if translation key not found
        })
      : notification.title;
    
    const message = notification.message_key
      ? t(notification.message_key, { 
          ...notification.translation_params,
          defaultValue: notification.message // Fallback if translation key not found
        })
      : notification.message;

    return { title, message };
  };

  return translateNotification;
};

export default function NotificationItem({
  notification,
  isSelected,
  onSelect,
  onClick,
  onActionClick,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation(['notifications']);
  const translateNotification = useNotificationTranslation();

  // Translate notification with fallback to database text
  const { title, message } = translateNotification(notification);

  const handleItemClick = (e) => {
    // Don't select if clicking on action buttons
    if (e.target.closest('button')) {
      return;
    }
    onSelect(notification.notification_id);
  };

  const isExpired = notification.expires_at && new Date(notification.expires_at) < new Date();
  const priorityColors = getPriorityColors(notification.priority, colors);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        p: 2.5,
        cursor: 'pointer',
        backgroundColor: notification.is_read ? 'transparent' : theme.palette.action.hover,
        borderLeft: notification.is_read ? 'none' : `3px solid ${colors.primary[500]}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        transition: theme.transitions.create(['background-color'], {
          duration: theme.transitions.duration.short,
        }),
        '&:hover': {
          backgroundColor: theme.palette.action.selected,
        },
        '&:last-child': {
          borderBottom: 'none',
        }
      }}
      onClick={handleItemClick}
    >
      {/* Column 1: Checkbox */}
      <Checkbox
        checked={isSelected}
        onChange={() => onSelect(notification.notification_id)}
        size="small"
        sx={{ 
          p: 0,
          mt: 1.7, // Align with first line of text
          color: colors.grey[500],
          '&.Mui-checked': {
            color: colors.primary[500],
          }
        }}
      />

      {/* Column 2: Icon */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: colors.grey[800],
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mt: 0.25, // Align with first line of text
        }}
      >
        {getNotificationIcon(notification.notification_type, colors)}
      </Box>

      {/* Column 3: Text Content (Title + Description + Metadata) */}
      <Box sx={{ flex: 1, minWidth: 0, mt: 1.3 }}>
        {/* Title and Description grouped together */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: notification.is_read ? 400 : 600,
              color: notification.is_read ? theme.palette.text.secondary : theme.palette.text.primary,
              lineHeight: 1.3,
              mb: 0.25,
            }}
          >
            {title}
          </Typography>
          
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {message}
          </Typography>
        </Box>

        {/* Metadata row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.disabled,
              fontSize: '0.8125rem',
            }}
          >
            <LocalizedDateDistance date={notification.created_at} />
          </Typography>

          {/* Priority Chip - translated */}
          <Chip
            label={t(`notifications:priority.${notification.priority}`)}
            size="small"
            sx={{
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'lowercase',
              backgroundColor: priorityColors.backgroundColor,
              color: priorityColors.color,
              border: `1px solid ${priorityColors.borderColor}`,
              '& .MuiChip-label': {
                px: 1,
              }
            }}
          />

          {/* Type Chip - translated */}
          <Chip
            label={t(`notifications:notification_types.${notification.notification_type}`, {
              defaultValue: notification.notification_type.replace('_', ' ')
            })}
            size="small"
            variant="outlined"
            sx={{
              height: 24,
              fontSize: '0.75rem',
              textTransform: 'capitalize',
              color: colors.grey[400],
              borderColor: theme.palette.divider,
              backgroundColor: 'transparent',
              '& .MuiChip-label': {
                px: 1,
              }
            }}
          />

          {!notification.is_read && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: colors.primary[500],
              }}
            />
          )}

          {isExpired && (
            <Chip
              label={t('notifications:messages.notification_expired')}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.75rem',
                fontWeight: 500,
                backgroundColor: colors.yellowAccent[900],
                color: colors.yellowAccent[400],
                border: `1px solid ${colors.yellowAccent[700]}`,
                '& .MuiChip-label': {
                  px: 1,
                }
              }}
            />
          )}
        </Box>
      </Box>

      {/* Column 4: Chips and Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {/* Action buttons */}
        {notification.onclick_url && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              window.open(notification.onclick_url, '_blank');
            }}
            title="Open in new tab"
            sx={{
              p: 1,
              color: colors.grey[400],
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                color: colors.primary[500],
              }
            }}
          >
            <OpenInNew sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onActionClick(notification, e);
          }}
          sx={{
            p: 1,
            color: colors.grey[400],
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            }
          }}
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Expiration warning (spans full width below) */}
      {isExpired && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: -40,
            mx: 2.5,
            p: 1.5,
            borderRadius: theme.shape.borderRadius,
            backgroundColor: colors.yellowAccent[900],
            border: `1px solid ${colors.yellowAccent[700]}`,
            zIndex: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: colors.yellowAccent[300],
              fontSize: '0.8125rem',
            }}
          >
            {t('notifications:messages.notification_expired')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}